import request from '../request';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 600;
const RESULT_TYPES = ['ticket', 'tour', 'article', 'voucher'];

const emptyResults = () => {
  const results = {};
  RESULT_TYPES.forEach((type) => {
    results[type] = [];
  });
  return results;
};

let debounceTimer = null;
let pendingRequest = null;

const cancelDebounce = () => {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
};

const cancelPending = () => {
  cancelDebounce();
  if (pendingRequest !== null) {
    pendingRequest.abort();
    pendingRequest = null;
  }
};

export default {
  namespaced: true,
  state: {
    query: '',
    requestedQuery: '',
    results: emptyResults(),
    loading: false,
    loaded: false,
  },
  mutations: {
    setQuery(state, query) {
      state.query = query;
    },
    setRequestedQuery(state, requestedQuery) {
      state.requestedQuery = requestedQuery;
    },
    setResults(state, results) {
      state.results = results;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setLoaded(state, loaded) {
      state.loaded = loaded;
    },
  },
  actions: {
    setQuery(context, value) {
      context.commit('setQuery', value);
      const query = value.trim();

      if (query.length < MIN_QUERY_LENGTH) {
        cancelPending();
        context.commit('setRequestedQuery', '');
        context.commit('setResults', emptyResults());
        context.commit('setLoading', false);
        context.commit('setLoaded', false);
        return;
      }

      if (query === context.state.requestedQuery) {
        cancelDebounce();
        context.commit('setLoading', pendingRequest !== null);
        return;
      }

      if (pendingRequest !== null) {
        context.commit('setRequestedQuery', '');
      }
      cancelPending();
      context.commit('setLoading', true);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        context.dispatch('run', query);
      }, DEBOUNCE_MS);
    },

    run(context, value) {
      const query = typeof value === 'undefined' ? context.state.query.trim() : value.trim();

      if (query.length < MIN_QUERY_LENGTH) {
        return Promise.resolve();
      }

      cancelPending();
      context.commit('setRequestedQuery', query);
      context.commit('setLoading', true);

      const { app } = context.rootState;
      const url = `${app.API_URL}${context.rootState.language}/${app.endpoints.SEARCH}`;

      return new Promise((resolve) => {
        pendingRequest = request.make(
          {
            type: 'GET',
            url,
            data: { q: query },
            background: true,
          },
          (err, res) => {
            pendingRequest = null;

            if (context.state.requestedQuery !== query) {
              resolve();
              return;
            }

            context.commit('setLoading', false);

            if (res && res.status === 200 && res.body && res.body.data) {
              const results = emptyResults();
              RESULT_TYPES.forEach((type) => {
                if (Array.isArray(res.body.data[type])) {
                  results[type] = res.body.data[type];
                }
              });
              context.commit('setResults', results);
              context.commit('setLoaded', true);
            } else {
              context.commit('setResults', emptyResults());
              context.commit('setLoaded', true);
            }
            resolve();
          }
        );
      });
    },

    clear(context) {
      cancelPending();
      context.commit('setQuery', '');
      context.commit('setRequestedQuery', '');
      context.commit('setResults', emptyResults());
      context.commit('setLoading', false);
      context.commit('setLoaded', false);
    },
  },
  getters: {
    ticketEvents(state, getters, rootState) {
      const events = rootState.tickets.data.events || {};
      const seen = [];
      state.results.ticket.forEach((ticket) => {
        const { eventId } = ticket;
        if (typeof eventId === 'undefined' || seen.includes(eventId)) {
          return;
        }
        if (typeof events[eventId] === 'undefined') {
          return;
        }
        seen.push(eventId);
      });
      return seen.map((eventId) => events[eventId]);
    },
    resultCount(state, getters) {
      const listed = RESULT_TYPES.filter((type) => type !== 'ticket').reduce(
        (count, type) => count + state.results[type].length,
        0
      );
      return getters.ticketEvents.length + listed;
    },
    hasResults(state, getters) {
      return getters.resultCount > 0;
    },
  },
};

export { MIN_QUERY_LENGTH, DEBOUNCE_MS, RESULT_TYPES };
